import { Chip, List, OverlayTrigger } from '@hai/ui-react';
import { isNil } from 'ramda';
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import defaultThumbnail from 'src/images/thumbnail-default.jpg';
import BusyIcon from 'src/js/component/base/busy-icon';
import { triggerEvent, watchEvent } from 'src/js/events';
import { useMutable } from 'src/js/hook/use-mutable';
import { useStores } from 'src/js/hook/use-stores';
import { scrollPanelIntoView } from 'src/js/component/actionBar/streaming-action-bar';
import videoInputs from 'src/js/pages/streaming/video-input/video-input-store';
import { generateDataAuto } from 'src/js/util/automation';
import { constant, PRODUCT } from 'src/js/constant';

import { VideoEncoderEdit } from './video-encoder-edit';
import {
  VIDIN_CONNECTOR,
  VideoEncoderState,
  VideoEncoderViewModel,
  encoderIsStopped,
  vidEncStateToColor,
  vidEncStateToIcon,
} from './video-encoder-models';
import { VideoEncoderStatistics } from './video-encoder-statistics';
import { startStopVideoEncoder } from './video-encoder-store';
import { StreamEncapsulation, StreamState } from '../stream/stream-models';
import streams from '../stream/stream-store';

interface Props {
  model: VideoEncoderViewModel;
  onSelect: (item: VideoEncoderViewModel, selected: boolean) => void;
  scrollToId: number | string;
}

// TODO many enum would be cleaner
const STATISTICS_TXT = 'STATISTICS';
const SETTINGS_TXT = 'SETTINGS';

export const VidencRow: React.FunctionComponent<Props> = ({ model, onSelect, scrollToId }) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const location = useLocation();
  const showStats = location?.state?.stats === true;

  const scroll = location?.state?.id === model.id || scrollToId === model.id;

  const [isExpanded, setIsExpanded] = useState<boolean>(scroll);
  const [collapseTimeoutId, setCollapseTimeoutId] = useMutable(null);
  const [expandContent, setExpandContent] = useState<string>(
    scroll ? (showStats ? STATISTICS_TXT : SETTINGS_TXT) : null,
  );
  const onExpandedStateChange = (expandedState: { eventKey: any; expanded: boolean }) => {
    if (isExpanded !== expandedState.expanded) {
      setIsExpanded(expandedState.expanded);
    }
  };

  watchEvent('expandAll:edit', () => {
    setExpandContent(SETTINGS_TXT);
    setIsExpanded(true);
  });
  watchEvent('expandAll:stats', () => {
    setExpandContent(STATISTICS_TXT);
    setIsExpanded(true);
  });
  const collapseAll = () => {
    setIsExpanded(false);
    setCollapseTimeoutId(
      setTimeout(() => {
        // the component needs to stay rendered during close for smooth closing
        setExpandContent(null);
        setCollapseTimeoutId(null);
      }, constant.collapseTime),
    );
  };
  watchEvent('collapseAll:edit', collapseAll);
  watchEvent('collapseAll:stats', collapseAll);

  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const onSelectHandler = (eventKey: string, expanded: boolean, ev: KeyboardEvent) => {
    switch (eventKey) {
      case 'STATISTICS':
      case 'SETTINGS':
        if (ev && ev.shiftKey) {
          // expend all
          if (expanded) {
            eventKey === 'SETTINGS' ? triggerEvent('expandAll:edit') : triggerEvent('expandAll:stats');
          } else {
            eventKey === 'SETTINGS' ? triggerEvent('collapseAll:edit') : triggerEvent('collapseAll:stats');
          }
          return;
        }
        setExpandContent(eventKey);
        if (expanded) {
          setIsExpanded(true);
          if (collapseTimeoutId() !== null) {
            clearTimeout(collapseTimeoutId());
            setCollapseTimeoutId(null); // prevent closing of panel
          }
          scroll && scrollPanelIntoView({ id: model.id });
        } else {
          setIsExpanded(false);
          setCollapseTimeoutId(
            setTimeout(() => {
              // the component needs to stay rendered during close for smooth closing
              setExpandContent(null);
              setCollapseTimeoutId(null);
            }, constant.collapseTime),
          );
        }
        break;
      case 'PLAY':
        // console.log('START/STOP video encoder', model);
        setIsUpdating(true);
        if (!encoderIsStopped(model.state)) {
          setRealThumbnailHidden(true);
          setDefaultThumbnailHidden(false);
        }
        startStopVideoEncoder(model, encoderIsStopped(model.state), false, t).then(() => {
          triggerEvent('preset-update');
          setIsUpdating(false);
        });
        break;
      default:
        break;
    }
  };
  useEffect(() => {
    return () => {
      // final cleanup
      if (collapseTimeoutId() !== null) {
        clearTimeout(collapseTimeoutId());
      }
    };
  }, []);

  // thumbnail refresh
  const [thumbnailDate, setThumbnailDate] = useState<string>(`${new Date().getTime()}`);
  useEffect(() => {
    let endrun = false;
    let timer: any;
    const triggerRefresh = () => {
      if (!endrun) {
        setThumbnailDate(`${new Date().getTime()}`);
        if (model.previewIntervalSec >= 1) {
          timer = setTimeout(triggerRefresh, model.previewIntervalSec * 1000);
        }
      }
    };

    timer = setTimeout(() => {
      triggerRefresh();
    }, model.previewIntervalSec * 1000);

    return () => {
      // cleanup
      endrun = true;
      clearTimeout(timer);
    };
  }, [model.previewIntervalSec]);

  const snapshotRef = useRef<HTMLImageElement>();
  const [defaultThumbnailHidden, setDefaultThumbnailHidden] = useState(false);
  const [realThumbnailHidden, setRealThumbnailHidden] = useState(true);
  const onLoadThumbnail = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    if (e.type !== 'error' && snapshotRef?.current) {
      snapshotRef.current.style.visibility = 'visible';
      if (model.state !== VideoEncoderState.STOPPED) { 
        setDefaultThumbnailHidden(true);
        setRealThumbnailHidden(false);
      }
      return;
    }
    setDefaultThumbnailHidden(false);
    setRealThumbnailHidden(true);
  };

  useEffect(() => {
    if (model.state !== VideoEncoderState.WORKING || !model.previewEnabled) {
      if (model.state !== VideoEncoderState.STOPPED) {
        setDefaultThumbnailHidden(false);
        setRealThumbnailHidden(true);
      }
    }
  }, [model.state, model.previewEnabled]);

  const btnId = `videnc-settings-${model.id}`;
  const handleCancelEdit = () => {
    const btn = document.getElementById(btnId);
    if (!isNil(btn)) {
      btn.click();
    }
  };

  const onClickListPanel = (ev: UIEvent) => {
    ev?.stopPropagation();
    ev?.preventDefault();
    if (
      (ev.target as any)?.className !== undefined /*
      (ev.target as any)?.className?.includes?.('HaiListPanelTitle') ||
      (ev.target as any)?.className?.includes?.('list-panel-main-content')*/
    ) {
      if (expandContent == null) {
        setExpandContent('SETTINGS');
      }
      // so we dont expand empty content
      setIsExpanded(!isExpanded);
    }
  };

  const statusColor = vidEncStateToColor(model.state);

  let interfaceTxtImproved = model.interfaceTxt;
  let sqdAddon = '';
  if (constant.product === PRODUCT.FX) {
    if (model.inputInterface === VIDIN_CONNECTOR.HDMI) {
      interfaceTxtImproved = 'HDMI';
    }
    if (model.inputInterface === VIDIN_CONNECTOR.SDI) {
      interfaceTxtImproved = 'SDI';
    }
    if (model.inputInterface === VIDIN_CONNECTOR.DISPLAYPORT) {
      interfaceTxtImproved = 'DisplayPort';
    }
  } else if (model.interface === 0) {
    if (videoInputs.length > 0) {
      interfaceTxtImproved = videoInputs[0].name;
      if (model.videoInputState === 'SQD ACTIVE') {
        sqdAddon = ' SQD';
      } else if (model.videoInputState === 'SQD FAILED') {
        sqdAddon = ''; // nothing for now but on expanded cfg row it will appear
      } else if (model.videoInputState === '2SI ACTIVE') {
        sqdAddon = ' 2SI';
      } else if (model.videoInputState === '2SI FAILED') {
        sqdAddon = ''; // nothing for now but on expanded cfg row it will appear
      }
    }
  }

  const associatedStreams = streams.filter((s) => s.video && s.video.length > 0 && s.video[0].id === model.id);
  //console.log('associatedStreams', associatedStreams);
  const anyStartedWhipStream = associatedStreams.find((s) => s.encapsulation === StreamEncapsulation.WHIP && s.state !== StreamState.STOPPED) !== undefined;
  const stopIsDisabledForWhip = !encoderIsStopped(model.state) && anyStartedWhipStream;
  //console.log('stopIsDisabledForWhip', stopIsDisabledForWhip, anyStartedWhipStream, associatedStreams);
  const overlayText = !defaultThumbnailHidden && (model.state !== VideoEncoderState.WORKING || !model?.previewEnabled) ? model.stateString : undefined;
  // console.log('overlayText', overlayText, defaultThumbnailHidden, model.state, model.previewEnabled);
  return (
    <div
      id={`panel-${model.id}`}
      className={model.hidden ? 'mk-row-hidden' : ''}
      data-auto={generateDataAuto('row', model.hidden ? 'hidden' : 'visible')}
      style={{ scrollMarginTop: '21px' }}
    >
      <List.Panel
        onClick={onClickListPanel}
        onSelect={(selected: boolean) => onSelect(model, selected)}
        checked={model.selected}
        panelColor={statusColor}
        expandedState={{ eventKey: expandContent, expanded: isExpanded }}
        onExpandedStateChange={onExpandedStateChange}
      >
        <List.PanelTitle
          onClick={onClickListPanel}
          useFsMask={true}
          title={model.name}
          statusIcon={vidEncStateToIcon(model.state)}
          iconColor={statusColor}
        />
        {model.state === VideoEncoderState.WORKING && model?.previewEnabled && (
          <List.Thumbnail
            useFsMask
            key="real-thumbnail"
            src={`/apis/preview/${model.interface}?t=${thumbnailDate}`}
            onError={onLoadThumbnail}
            onLoad={onLoadThumbnail}
            componentRef={snapshotRef}
            hidden={model.hidden || realThumbnailHidden}
          />
        )}
        <List.Thumbnail
          key="fake-thumbnail"
          src={defaultThumbnail}
          hidden={model.hidden || defaultThumbnailHidden}
          overlayText={overlayText}
          overlayStyles={{ backgroundColor: 'transparent' }}
        />
        <List.PanelDetail>
          <List.PanelDetailItem
            divider="vertical"
            text={t('general.id')}
            data-auto={generateDataAuto('list_panel', 'detail id')}
          >
            <Chip size="sm">{model.id.toString()}</Chip>
          </List.PanelDetailItem>
          <List.PanelDetailItem
            divider="vertical"
            text={`${interfaceTxtImproved} (${model.inputFormat}${sqdAddon})`}
            icon="Input"
            data-auto={generateDataAuto('list_panel', 'detail input')}
          />
          <List.PanelDetailItem
            divider="vertical"
            text={model.outputResolution}
            data-auto={generateDataAuto('list_panel', 'detail output resolution')}
          />
          <List.PanelDetailItem
            divider="vertical"
            text={model.codecTxt}
            data-auto={generateDataAuto('list_panel', 'detail codec')}
          />
          <List.PanelDetailItem
            text={`${model.bitrate}kbps`}
            data-auto={generateDataAuto('list_panel', 'detail bitrate')}
          />
        </List.PanelDetail>
        {sessionStore.isUser() ? (
          <List.Actions>
            <List.ActionItem
              expandButton
              eventKey={STATISTICS_TXT}
              icon="ReportStats"
              title={t('general.statistics')}
              onSelect={onSelectHandler}
            />
            <List.ActionItem
              expandButton
              eventKey={SETTINGS_TXT}
              icon="Settings"
              title={t('general.settings')}
              onSelect={onSelectHandler}
              id={btnId}
            />
          </List.Actions>
        ) : (
          <List.Actions>
            <List.ActionItem
              expandButton
              eventKey={STATISTICS_TXT}
              icon="ReportStats"
              title={t('general.statistics')}
              onSelect={onSelectHandler}
            />
            <List.ActionItem
              expandButton
              eventKey={SETTINGS_TXT}
              icon="Settings"
              title={t('general.settings')}
              onSelect={onSelectHandler}
              id={btnId}
            />
            <List.ActionItem isDivider />
            <List.ActionItem
              eventKey="PLAY"
              icon={
                <OverlayTrigger popoverContent={stopIsDisabledForWhip ? t('videnc.tooltips.stopVideoEncoderWithWhip') : undefined} placement="top" onClick={(e:any) => stopIsDisabledForWhip && e.stopPropagation()} >
                <BusyIcon
                  isActive={!encoderIsStopped(model.state)}
                  activeIcon="StopFilled"
                  inactiveIcon="PlayFilled"
                  busy={isUpdating}
                  disabled={stopIsDisabledForWhip}
                />
                </OverlayTrigger>
              }
              showActive={false}
              onSelect={stopIsDisabledForWhip ? undefined : onSelectHandler}
              disabled={isUpdating}
            />
          </List.Actions>
        )}
        <List.ExpandedPanel>
          {expandContent === 'STATISTICS' && isExpanded && <VideoEncoderStatistics model={model} />}
          {expandContent === 'SETTINGS' && isExpanded && <VideoEncoderEdit model={model} onCancel={handleCancelEdit} />}
        </List.ExpandedPanel>
      </List.Panel>
    </div>
  );
};
